// src/app/components/helper/user-settings-apply.ts
import type { SettingsData } from "@/app/services/common/settings-service";

/**
 * Applies user settings globally:
 * - data-theme
 * - reduced-motion/high-contrast classes
 * - font size (CSS vars, proportionally scaled against the 14px design baseline)
 * - stores display prefs for later formatting
 */
export function applyUserSettingsToDom(settings: SettingsData) {
  if (typeof document === "undefined") return;

  const root = document.documentElement;

  const theme = settings?.appearance?.theme || "light";
  root.setAttribute("data-theme", theme);

  root.classList.toggle("reduced-motion", !!settings?.accessibility?.reducedMotion);
  root.classList.toggle("high-contrast", !!settings?.accessibility?.highContrast);

  const base = clampNumber(settings?.accessibility?.fontSize ?? 16, 14, 20);
  applyFontScaleVars(base);

  try {
    localStorage.setItem(
      "up_settings_number_format",
      settings.appearance?.numberFormat || "1,234.56 (US)"
    );
    localStorage.setItem(
      "up_settings_date_format",
      settings.appearance?.dateFormat || "MM/DD/YYYY (US)"
    );
    localStorage.setItem(
      "up_settings_decimal_precision",
      String(settings.appearance?.decimalPrecision ?? 2)
    );
  } catch {
    // ignore
  }
}

/**
 * Design-system token sizes at the 14px baseline (matches globals.css).
 * The slider controls `--text-base`; every other token scales proportionally so
 * the visual hierarchy defined in the theme is preserved at any base value.
 */
const DESIGN_TOKENS_AT_14 = {
  xs: 11,
  sm: 13,
  base: 14,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 30,
  "4xl": 40,
} as const;

export function applyFontScaleVars(basePx: number) {
  const root = document.documentElement;

  // Proportional scale against the design system baseline.
  // At basePx = 14 → every token matches the theme exactly.
  // At basePx = 16 → everything is ~14% larger; at 20 → ~43% larger.
  const scale = basePx / DESIGN_TOKENS_AT_14.base;

  // Round to 1 decimal to avoid visually noisy sub-pixel values but still
  // allow the 0.1-step slider to produce meaningful differences.
  const scaled = (tokenPx: number): string =>
    `${Math.round(tokenPx * scale * 10) / 10}px`;

  root.style.setProperty("--text-xs", scaled(DESIGN_TOKENS_AT_14.xs));
  root.style.setProperty("--text-sm", scaled(DESIGN_TOKENS_AT_14.sm));
  root.style.setProperty("--text-base", `${basePx}px`); // exact — no rounding on the base
  root.style.setProperty("--text-lg", scaled(DESIGN_TOKENS_AT_14.lg));
  root.style.setProperty("--text-xl", scaled(DESIGN_TOKENS_AT_14.xl));
  root.style.setProperty("--text-2xl", scaled(DESIGN_TOKENS_AT_14["2xl"]));
  root.style.setProperty("--text-3xl", scaled(DESIGN_TOKENS_AT_14["3xl"]));
  root.style.setProperty("--text-4xl", scaled(DESIGN_TOKENS_AT_14["4xl"]));
}

function clampNumber(v: number, min: number, max: number): number {
  if (!Number.isFinite(v)) return min;
  return Math.min(max, Math.max(min, v));
}